import { LitElement, html, css, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BranchQuoteSupport } from '../../../api/dealer-api-interface-franchisee';
import { LangBranchQuoteConversation } from '../../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-conversation-lang';
import { LangBranchQuoteSupportNew } from '../../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
import { FranchiseeQuoteContainerManager } from '../../quotes/data/franchisee-quote-manager';
import {
  FranchiseeQuoteItemSelectorTable,
  QuoteItemSelectorModal
} from '../../quotes/views/franchisee-quote-item-table';
import { FormInputAssistantBase } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant-base';
import { SupportTicketService } from '../support-ticket-service';
import { SupportQuoteItemLinkDetail } from '../../../api/dealer-api-interface-franchisee';
import { supportTitleAndSubjectMaxLength } from '../conversation-helper';

@customElement('wm-support-ticket-header')
export class WMSupportTicketHeader extends LitElement {
  // Use light DOM to inherit global styling identical to legacy inline template
  protected createRenderRoot() {
    return this; // disable shadow DOM
  }
  static styles = css`
    :host {
      display: block;
    }
  `; // rely on existing global conversation-header styles

  @property({ attribute: false }) ticket?: BranchQuoteSupport;
  @property({ attribute: false }) quoteManager?: FranchiseeQuoteContainerManager;

  @property({ type: Boolean }) readonly = false;
  // Always render header & subject; flags removed (were always true)
  @property({ type: Boolean }) showQuoteItemLink = true;
  /** Optional injected service (preferred). Falls back to quoteManager.supportTicketService if omitted */
  @property({ attribute: false }) supportService?: SupportTicketService;
  /** Local cached selected quote item keys (derived from ticket initially) */
  @state()
  private linkedQuoteItemKeys: string[] = [];

  updated(changed: Map<string, unknown>): void {
    if (changed.has('ticket')) {
      this.linkedQuoteItemKeys = this.extractLinkedKeys();
    }
  }

  private extractLinkedKeys(): string[] {
    if (!this.ticket || !(this.ticket as any).quoteItemLink) return [];
    return Object.entries((this.ticket as any).quoteItemLink)
      .filter(([_, v]) => (v as { isLinked?: boolean }).isLinked)
      .map(([k]) => k);
  }

  private onSubjectChange(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    const service = this.supportService ?? this.quoteManager?.supportTicketService;
    if (service && this.ticket) {
      service.updateDraftMetadata(this.ticket.id, { subject: value });
    }
  }

  private async requestLinkEdit(e: Event) {
    if (this.readonly || !this.quoteManager || !this.ticket) return;
    const modal = new QuoteItemSelectorModal(this.quoteManager, this.linkedQuoteItemKeys);
    await modal.showModal();
    if (!modal.result?.success) return;
    const newKeys = modal.result.keys;
    // Short-circuit if unchanged
    const prev = this.linkedQuoteItemKeys.slice().sort().join('|');
    const next = [...newKeys].sort().join('|');
    if (prev === next) return;
    this.linkedQuoteItemKeys = newKeys;
    // Update UI table selection immediately (optimistic)
    this.updateDisplayedSelectedKeys(e);
    // Build link detail map
    const linkMap = newKeys.reduce<Record<string, SupportQuoteItemLinkDetail>>((acc, key) => {
      acc[key] = {
        id: key,
        isLinked: true,
        textDisplay: this.quoteManager?.makeSupportLinkDisplayValueForQuoteItemId(key) ?? ''
      } as any;
      return acc;
    }, {});
    // Store in draft (centralized merge on future mutation)
    const service = this.supportService ?? this.quoteManager?.supportTicketService;
    if (service && this.ticket) service.updateDraftMetadata(this.ticket.id, { quoteItemLinks: linkMap });
  }

  private updateDisplayedSelectedKeys(e: Event) {
    const header = (e.target as HTMLElement).closest('.conversation-header');
    if (!header) return;
    const table = header.querySelector<FranchiseeQuoteItemSelectorTable>('#table');
    table?.setSelectedItems(this.linkedQuoteItemKeys);
  }

  render(): TemplateResult {
    // --- Begin: Copied logic from getFullHeaderTemplate (presume showHeader/showSubject true) ---
    const forms = new FormInputAssistantBase(() => this.readonly);

    // linkedQuoteItemKeys logic
    const linkKeys = this.linkedQuoteItemKeys;
    const linkKeysTemplate =
      linkKeys.length > 0 && this.quoteManager && this.showQuoteItemLink
        ? html`<div class="quote-item-selector-container">
            <franchisee-quote-item-selector-table
              id="table"
              .readonly=${true}
              .allowOpenLink=${true}
              .quoteManager=${this.quoteManager as any}
              .showSelectedOnly=${true}
              .quoteItemKeys=${linkKeys}
              @wm-component-ready=${(e: CustomEvent<FranchiseeQuoteItemSelectorTable>) => {
                const table = e.detail;
                table?.setSelectedItems(linkKeys);
              }}
            ></franchisee-quote-item-selector-table>
          </div>`
        : html``;
    const isReadonly = this.readonly;
    const links = this.showQuoteItemLink
      ? html` <div class="d-flex conversation-link-wrapper">
            ${forms.multiEditRow({
              title: LangBranchQuoteSupportNew.linkQuoteItems(),
              toolTip: LangBranchQuoteSupportNew.linkQuoteItemsLabel(),
              slotted: isReadonly
                ? html`<webmodule-button size="small" variant="default" disabled>
                    ${LangBranchQuoteSupportNew.updateLinkQuoteItems()}
                  </webmodule-button>`
                : html`<webmodule-button size="small" variant="primary" @click=${(e: Event) => this.requestLinkEdit(e)}>
                    ${LangBranchQuoteSupportNew.updateLinkQuoteItems()}
                  </webmodule-button>`
            })}
          </div>
          <div class="d-flex conversation-link-wrapper">${linkKeysTemplate}</div>`
      : html``;
    // Effective subject value: draft overrides ticket
    const service = this.supportService ?? this.quoteManager?.supportTicketService;
    const effectiveSubject = (() => {
      if (!service || !this.ticket) return this.ticket?.subject ?? '';
      const session = service.getSession(this.ticket.id); // allowed: read-only session access
      if (session.draftMeta && session.draftMeta.touched.subject) return session.draftMeta.subject ?? '';
      return session.baselineMeta?.subject ?? this.ticket.subject ?? '';
    })();
    const subject = html` <div class="d-flex conversation-subject-wrapper align-items-center">
      <webmodule-input
        id="subject"
        class="conversation-subject webmodule-control-left-label"
        type="text"
        label="${LangBranchQuoteConversation.supportSubject()}:"
        maxlength="${supportTitleAndSubjectMaxLength}"
        size="small"
        ?disabled=${isReadonly}
        .value=${effectiveSubject}
        @webmodule-change=${this.onSubjectChange}
        @webmodule-input=${this.onSubjectChange}
      ></webmodule-input>
    </div>`;
    return html` <div class="conversation-header mb-3">${subject}${links}</div>`;
    // --- End: Copied logic from getFullHeaderTemplate ---
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wm-support-ticket-header': WMSupportTicketHeader;
  }
}
